import { z } from 'zod';

/**
 * Row payload contracts for `agents_session_index` — the small per-agent
 * Postgres index of key session events (anchored to Axon stream sequences)
 * that turn-aligned session pagination reads its page boundaries from.
 *
 * Each row is `(type, data)` with `data` discriminated by `type`. The
 * vocabulary is deliberately open: readers parse rows at the boundary with
 * {@link parseSessionIndexEntry} and skip rows they do not understand, so a
 * newer writer can add row types without breaking older readers.
 */

/**
 * Maximum stored length of a `prompt` row's text, enforced at write time.
 * The unit is UTF-16 code units (what `string.length` and Zod's `.max`
 * count); writers must truncate without splitting a surrogate pair, so a
 * stored text is always well-formed and at most this many code units.
 */
export const SESSION_INDEX_PROMPT_TEXT_MAX_LENGTH = 64;

/**
 * The protocols whose native prompt events anchor `prompt` rows, as stored
 * in `data.protocolType`. Exported so readers switch on these constants
 * rather than bare strings. The schema field stays an open `string` for
 * forward compat — treat values outside this list as "unknown protocol",
 * not an error.
 */
export const SESSION_INDEX_PROMPT_PROTOCOL_TYPES = ['claude', 'acp', 'codex'] as const;
export type SessionIndexPromptProtocolType = (typeof SESSION_INDEX_PROMPT_PROTOCOL_TYPES)[number];

/**
 * How the indexed turn ended. `completed` / `errored` / `interrupted` mirror
 * the turn-end lifecycle vocabulary; `suspended` / `shutdown` record a devbox
 * forcing an open turn closed without a protocol turn-end signal.
 */
export const SessionIndexTurnEndReasonSchema = z.enum([
  'completed',
  'errored',
  'interrupted',
  'suspended',
  'shutdown',
]);
export type SessionIndexTurnEndReason = z.infer<typeof SessionIndexTurnEndReasonSchema>;

/**
 * Token counters captured at a turn end, when the protocol reported them.
 * Fields are nullable as well as optional so extractor outputs (which use
 * `null` for "not reported") store without translation.
 *
 * Semantics are the anchoring turn-end payload's `usage` object — for
 * claude that is the primary model's counts for the last main-loop turn
 * (it undercounts subagent work relative to the `modelUsage` breakdown;
 * see `extractModelUsageTotals` in plugin-api). Stored as reported, not
 * normalized across protocols.
 */
export const SessionIndexTokenUsageSchema = z.object({
  inputTokens: z.number().nullable().optional(),
  outputTokens: z.number().nullable().optional(),
  cacheReadTokens: z.number().nullable().optional(),
  cacheWriteTokens: z.number().nullable().optional(),
});
export type SessionIndexTokenUsage = z.infer<typeof SessionIndexTokenUsageSchema>;

/**
 * A user prompt, anchored at the protocol's native prompt event (`query` for
 * claude, the `session/prompt` request echo for ACP, `turn/start` /
 * `turn/steer` for codex). `text` is pre-truncated to
 * {@link SESSION_INDEX_PROMPT_TEXT_MAX_LENGTH} at write time — it feeds the
 * session nav rail directly from Postgres. `opensTurn: false` marks mid-turn
 * injections (codex `turn/steer`): they get a rail entry but are not page
 * boundaries.
 */
export const SessionIndexPromptEntrySchema = z.object({
  type: z.literal('prompt'),
  data: z.object({
    text: z.string().max(SESSION_INDEX_PROMPT_TEXT_MAX_LENGTH),
    opensTurn: z.boolean(),
    protocolType: z.string(),
  }),
});
export type SessionIndexPromptEntry = z.infer<typeof SessionIndexPromptEntrySchema>;

/**
 * A turn boundary's closing edge, anchored at the event that ended the turn
 * (native protocol end, broker `turn.*` fallback, or a devbox
 * suspend/shutdown while a turn was open). Exactly one per boundary.
 *
 * Cross-page tool-call stitching (recording which tool calls a segment
 * left open) is a planned additive field on this shape; nothing stores it
 * yet.
 */
export const SessionIndexTurnEndEntrySchema = z.object({
  type: z.literal('turn.end'),
  data: z.object({
    reason: SessionIndexTurnEndReasonSchema,
    durationMs: z.number().optional(),
    tokenUsage: SessionIndexTokenUsageSchema.optional(),
  }),
});
export type SessionIndexTurnEndEntry = z.infer<typeof SessionIndexTurnEndEntrySchema>;

export const SessionIndexEntrySchema = z.discriminatedUnion('type', [
  SessionIndexPromptEntrySchema,
  SessionIndexTurnEndEntrySchema,
]);
export type SessionIndexEntry = z.infer<typeof SessionIndexEntrySchema>;

/**
 * Every row `type` this schema version understands. Readers use it to tell
 * an unknown type (forward compat — skip silently) from a known type whose
 * `data` failed validation (corruption — skip, but worth a warning).
 */
export const SESSION_INDEX_ENTRY_TYPES: readonly SessionIndexEntry['type'][] =
  SessionIndexEntrySchema.options.map((option) => option.shape.type.value);

/**
 * Truncate a prompt to {@link SESSION_INDEX_PROMPT_TEXT_MAX_LENGTH} UTF-16
 * code units without splitting a surrogate pair: when the cut would land
 * between the halves of a pair, the trailing high surrogate is dropped too.
 * A plain `slice` there would store a lone surrogate, which is not valid
 * JSON text — Postgres jsonb rejects unpaired surrogate escapes, so the
 * row insert would fail deterministically on every replay.
 */
export function truncateSessionIndexPromptText(text: string): string {
  if (text.length <= SESSION_INDEX_PROMPT_TEXT_MAX_LENGTH) return text;
  const cut = text.slice(0, SESSION_INDEX_PROMPT_TEXT_MAX_LENGTH);
  const last = cut.charCodeAt(cut.length - 1);
  const isLoneHighSurrogate = last >= 0xd800 && last <= 0xdbff;
  return isLoneHighSurrogate ? cut.slice(0, -1) : cut;
}

/**
 * Parse one `agents_session_index` row's `(type, data)` at the read boundary.
 * Returns `null` — skip the row — instead of throwing when `type` is unknown
 * or `data` does not match its type's schema, so readers stay forward
 * compatible with row types added by newer writers.
 */
export function parseSessionIndexEntry(row: {
  type: string;
  data: unknown;
}): SessionIndexEntry | null {
  const result = SessionIndexEntrySchema.safeParse(row);
  return result.success ? result.data : null;
}
